/**
 * Linirio API client.
 * Handles app API calls with authentication.
 */
import { API_URL, apiConfig, apiRequest, formatApiError } from './client';
import { getActiveLocale } from '../../i18n/runtime';

export { apiConfig, formatApiError };

// ============== AUTH ==============

export const auth = {
  async register(email, password, name) {
    return apiRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, locale: getActiveLocale() }),
    });
  },
  
  async login(email, password) {
    return apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async verify2fa(challengeToken, code) {
    return apiRequest('/api/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    });
  },
  
  async logout() {
    return apiRequest('/api/auth/logout', { method: 'POST' });
  },
  
  async me() {
    return apiRequest('/api/auth/me');
  },
  
  async refresh() {
    return apiRequest('/api/auth/refresh', { method: 'POST' });
  },
  
  async forgotPassword(email) {
    return apiRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  
  async resetPassword(token, newPassword) {
    return apiRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },

  async changePassword(currentPassword, newPassword) {
    return apiRequest('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },
};

// ============== DIAGNOSTICS ==============

export const diagnostics = {
  async health() {
    return apiRequest('/health');
  },
  config: apiConfig,
};

// ============== USER ==============

export const user = {
  async update(data) {
    return apiRequest('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async security() {
    return apiRequest('/api/users/me/security');
  },

  async setup2fa() {
    return apiRequest('/api/users/me/2fa/setup', { method: 'POST' });
  },

  async enable2fa(code) {
    return apiRequest('/api/users/me/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async disable2fa(currentPassword, code) {
    return apiRequest('/api/users/me/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, code }),
    });
  },
  
  async updateOutreachSettings(settings) {
    return apiRequest('/api/users/me/outreach-settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },

  async updateOnboarding(action) {
    return apiRequest('/api/users/me/onboarding', {
      method: 'PATCH',
      body: JSON.stringify({ action }),
    });
  },
};

// ============== LINKEDIN ==============

export const linkedin = {
  async getConnectLink(accountId = null) {
    const query = accountId ? `?account_id=${encodeURIComponent(accountId)}` : '';
    return apiRequest(`/api/linkedin/connect-link${query}`);
  },
  
  async getAccounts() {
    return apiRequest('/api/linkedin/accounts');
  },

  async completeConnection(accountId) {
    return apiRequest('/api/linkedin/connect/complete', {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId }),
    });
  },
  
  async disconnectAccount(accountId) {
    return apiRequest(`/api/linkedin/accounts/${accountId}`, {
      method: 'DELETE',
    });
  },
};

// ============== ENGAGEMENT (READ-ONLY) ==============

export const engagement = {
  async createManualPost({ content, authorName = null, postUrl = null }) {
    return apiRequest('/api/engagement/manual-posts', {
      method: 'POST',
      body: JSON.stringify({ content, author_name: authorName, post_url: postUrl }),
    });
  },

  async listManualPosts(limit = 100) {
    return apiRequest(`/api/engagement/manual-posts?limit=${limit}`);
  },

  async deleteManualPost(postId) {
    return apiRequest(`/api/engagement/manual-posts/${encodeURIComponent(postId)}`, {
      method: 'DELETE',
    });
  },
  async getVoice() {
    return apiRequest('/api/users/me/engagement-voice');
  },

  async updateVoice(settings) {
    return apiRequest('/api/users/me/engagement-voice', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },

  async syncPosts() {
    return apiRequest('/api/engagement/sync-posts', {
      method: 'POST',
      body: JSON.stringify({
        limit_per_profile: 2,
        comments_limit: 100,
      }),
    });
  },

  async listPosts(view = 'todo', limit = 100) {
    return apiRequest(`/api/engagement/posts?view=${encodeURIComponent(view)}&limit=${limit}`);
  },

  async buildConcepts({ instructions = null, locale = 'en' } = {}) {
    return apiRequest('/api/engagement/concepts/bulk', {
      method: 'POST',
      body: JSON.stringify({ instructions, locale }),
    });
  },

  async regenerationPreview(sourceJobId = null) {
    const query = sourceJobId ? `?source_job_id=${encodeURIComponent(sourceJobId)}` : '';
    return apiRequest(`/api/engagement/concepts/regeneration-preview${query}`);
  },

  async regenerateConcepts({ sourceJobId = null, locale = 'en' } = {}) {
    return apiRequest('/api/engagement/concepts/regenerate', {
      method: 'POST',
      body: JSON.stringify({ source_job_id: sourceJobId, locale }),
    });
  },

  async listJobs(limit = 20) {
    return apiRequest(`/api/engagement/sync-jobs?limit=${limit}`);
  },

  async getJob(jobId) {
    return apiRequest(`/api/engagement/sync-jobs/${encodeURIComponent(jobId)}`);
  },

  async diagnostics() {
    return apiRequest('/api/engagement/diagnostics');
  },

  async buildConcept(postId, { instructions = null, locale = 'en' } = {}) {
    return apiRequest(`/api/engagement/posts/${encodeURIComponent(postId)}/concept`, {
      method: 'POST',
      body: JSON.stringify({ instructions, locale }),
    });
  },

  async updateConcept(postId, text, status = 'draft') {
    return apiRequest(`/api/engagement/posts/${encodeURIComponent(postId)}/concept`, {
      method: 'PATCH',
      body: JSON.stringify({ text, status }),
    });
  },

  async getScheduling() {
    return apiRequest('/api/users/me/engagement-scheduling');
  },

  async updateScheduling(settings) {
    return apiRequest('/api/users/me/engagement-scheduling', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },

  async getSendBatch(batchId) {
    return apiRequest(`/api/engagement/send-batches/${encodeURIComponent(batchId)}`);
  },

  async listSendBatches(limit = 10) {
    return apiRequest(`/api/engagement/send-batches?limit=${limit}`);
  },

  async updateSendBatch(batchId, action) {
    return apiRequest(`/api/engagement/send-batches/${encodeURIComponent(batchId)}/action`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  },
};

// ============== INBOX ==============

export const inbox = {
  async list(params = {}) {
    const query = new URLSearchParams();
    if (params.filter) query.set('filter', params.filter);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    
    return apiRequest(`/api/inbox?${query}`);
  },

  async getConversation(conversationId) {
    return apiRequest(`/api/inbox/${conversationId}`);
  },
  
  async getMessages(conversationId, limit = 50) {
    return apiRequest(`/api/inbox/${conversationId}/messages?limit=${limit}`);
  },
  
  async sendMessage(conversationId, text, useAi = false) {
    return apiRequest(`/api/inbox/${conversationId}/send`, {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId, text, use_ai: useAi }),
    });
  },
  
  async sync(deep = false) {
    return apiRequest(`/api/inbox/sync${deep ? '?deep=true' : ''}`, { method: 'POST' });
  },

  async patch(conversationId, updates) {
    return apiRequest(`/api/inbox/${conversationId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async remove(conversationId) {
    return apiRequest(`/api/inbox/${conversationId}`, { method: 'DELETE' });
  },

  async toLead(conversationId) {
    return apiRequest(`/api/inbox/${conversationId}/to-lead`, { method: 'POST' });
  },

  async attach(conversationId, file, text = '') {
    const formData = new FormData();
    formData.append('file', file);
    if (text) formData.append('text', text);
    return apiRequest(`/api/inbox/${conversationId}/attach`, {
      method: 'POST',
      body: formData,
    });
  },

  attachmentUrl(attachmentId, { download = false } = {}) {
    const q = download ? '?download=1' : '';
    return `${API_URL}/api/inbox/attachments/${attachmentId}${q}`;
  },

  imageProxyUrl(linkedinUrl) {
    if (!linkedinUrl) return null;
    return `${API_URL}/api/img-proxy?url=${encodeURIComponent(linkedinUrl)}`;
  },
};

// ============== LEADS ==============

export const leads = {
  async list(params = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    
    return apiRequest(`/api/leads?${query}`);
  },
  
  async importUrls(urls, autoOutreach = false, outreachTemplate = null) {
    return apiRequest('/api/leads/import/urls', {
      method: 'POST',
      body: JSON.stringify({
        urls,
        auto_outreach: autoOutreach,
        outreach_template: outreachTemplate,
      }),
    });
  },
  
  async importCsv(file, autoOutreach = false) {
    const formData = new FormData();
    formData.append('file', file);
    
    return apiRequest(`/api/leads/import/csv?auto_outreach=${autoOutreach}`, {
      method: 'POST',
      body: formData,
    });
  },
  
  async delete(leadId) {
    return apiRequest(`/api/leads/${leadId}`, { method: 'DELETE' });
  },

  async patch(leadId, updates) {
    return apiRequest(`/api/leads/${leadId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },
};

// ============== ADMIN ==============

export const admin = {
  async getUsers(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    
    return apiRequest(`/api/admin/users?${query}`);
  },
  
  async updateUser(userId, data) {
    return apiRequest(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  
  async deleteUser(userId) {
    return apiRequest(`/api/admin/users/${userId}`, { method: 'DELETE' });
  },
  
  async getStats() {
    return apiRequest('/api/admin/stats');
  },

  async setUserOpenAIKey(userId, openaiKey) {
    return apiRequest(`/api/admin/users/${userId}/openai-key`, {
      method: 'PUT',
      body: JSON.stringify({ openai_api_key: openaiKey }),
    });
  },

  async getUserOpenAIKeyStatus(userId) {
    return apiRequest(`/api/admin/users/${userId}/openai-key/status`);
  },

  async impersonate(userId) {
    return apiRequest(`/api/admin/impersonate/${userId}`, { method: 'POST' });
  },

  async exitImpersonation() {
    return apiRequest('/api/admin/impersonate/exit', { method: 'POST' });
  },

  async audit(limit = 100) {
    return apiRequest(`/api/admin/audit?limit=${limit}`);
  },

  async getUserPosts(userId, limit = 20) {
    return apiRequest(`/api/admin/users/${userId}/posts?limit=${limit}`);
  },

  async createPostForUser(userId, { text, linkUrl, file }) {
    const fd = new FormData();
    if (text) fd.append('text', text);
    if (linkUrl) fd.append('link_url', linkUrl);
    if (file) fd.append('file', file);
    return apiRequest(`/api/admin/users/${userId}/posts`, {
      method: 'POST',
      body: fd,
    });
  },

  async createPostDraftForUser(userId, payload) {
    // payload: { text, postType, visibility, linkUrl, articleTitle,
    //            pollOptions, pollDurationDays, scheduledAt, file, video }
    const fd = new FormData();
    if (payload.text) fd.append('text', payload.text);
    fd.append('post_type', payload.postType || 'text');
    fd.append('visibility', payload.visibility || 'anyone');
    if (payload.linkUrl) fd.append('link_url', payload.linkUrl);
    if (payload.articleTitle) fd.append('article_title', payload.articleTitle);
    (payload.pollOptions || []).forEach((opt) => fd.append('poll_options', opt));
    if (payload.pollDurationDays) fd.append('poll_duration_days', String(payload.pollDurationDays));
    if (payload.scheduledAt) fd.append('scheduled_at', payload.scheduledAt);
    if (payload.file) fd.append('file', payload.file);
    if (payload.video) fd.append('video', payload.video);
    if (payload.document) fd.append('document', payload.document);
    return apiRequest(`/api/admin/users/${userId}/posts/draft`, {
      method: 'POST',
      body: fd,
    });
  },

  async revisePostApproval(userId, approvalId, payload) {
    const fd = new FormData();
    if (payload.text) fd.append('text', payload.text);
    fd.append('post_type', payload.postType || 'text');
    fd.append('visibility', payload.visibility || 'anyone');
    if (payload.linkUrl) fd.append('link_url', payload.linkUrl);
    if (payload.articleTitle) fd.append('article_title', payload.articleTitle);
    (payload.pollOptions || []).forEach((opt) => fd.append('poll_options', opt));
    if (payload.pollDurationDays) fd.append('poll_duration_days', String(payload.pollDurationDays));
    if (payload.scheduledAt) fd.append('scheduled_at', payload.scheduledAt);
    if (payload.clearScheduledAt) fd.append('clear_scheduled_at', 'true');
    if (payload.clearExistingMedia) fd.append('clear_existing_media', 'true');
    if (payload.file) fd.append('file', payload.file);
    if (payload.video) fd.append('video', payload.video);
    if (payload.document) fd.append('document', payload.document);
    return apiRequest(`/api/admin/users/${userId}/posts/approvals/${approvalId}/revise`, {
      method: 'POST',
      body: fd,
    });
  },

  postApprovalMediaUrl(userId, approvalId, { download = false } = {}) {
    const q = download ? '?download=1' : '';
    return `${API_URL}/api/admin/users/${userId}/posts/approvals/${approvalId}/media${q}`;
  },
};

// ============== AI AUTORESPONDER ==============

export const ai = {
  async getSettings() {
    return apiRequest('/api/users/me/ai-settings');
  },
  async updateSettings(settings) {
    return apiRequest('/api/users/me/ai-settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },
  async rewrite(text, tone = 'professional') {
    return apiRequest('/api/ai/rewrite', {
      method: 'POST',
      body: JSON.stringify({ text, tone }),
    });
  },

  async replyDraft(conversationId, { tone, instructions } = {}) {
    return apiRequest('/api/ai/reply-draft', {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId, tone, instructions }),
    });
  },

  async backfillAutoReply(limit = 20) {
    return apiRequest(`/api/ai/autoreply/backfill?limit=${limit}`, {
      method: 'POST',
    });
  },

  async diagnostics() {
    return apiRequest('/api/ai/autoreply/diagnostics');
  },
};

// ============== KNOWLEDGE BASE ==============

export const knowledge = {
  async listSources() {
    return apiRequest('/api/knowledge/sources');
  },

  async createSource(data) {
    return apiRequest('/api/knowledge/sources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateSource(sourceId, data) {
    return apiRequest(`/api/knowledge/sources/${sourceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteSource(sourceId) {
    return apiRequest(`/api/knowledge/sources/${sourceId}`, {
      method: 'DELETE',
    });
  },

  async reindexSource(sourceId) {
    return apiRequest(`/api/knowledge/sources/${sourceId}/reindex`, {
      method: 'POST',
    });
  },
};

// ============== TASKS ==============

export const tasks = {
  async list(params = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.kind) query.set('kind', params.kind);
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    return apiRequest(`/api/tasks?${query}`);
  },

  async get(taskId) {
    return apiRequest(`/api/tasks/${taskId}`);
  },

  async update(taskId, data) {
    return apiRequest(`/api/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// ============== REPORTS ==============

export const reports = {
  async overview(days = 30) {
    return apiRequest(`/api/reports/overview?days=${days}`);
  },
};

// ============== BOOKINGS ==============

export const bookings = {
  async list(limit = 100) {
    return apiRequest(`/api/bookings?limit=${limit}`);
  },

  async create(data) {
    return apiRequest('/api/bookings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async update(bookingId, data) {
    return apiRequest(`/api/bookings/${bookingId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async cancel(bookingId) {
    return apiRequest(`/api/bookings/${bookingId}/cancel`, {
      method: 'POST',
    });
  },
};

// Backwards-compatible named export used by older screens.
export const Bookings = bookings;

// ============== CALENDAR ==============

export const calendar = {
  async status() {
    return apiRequest('/api/calendar/status');
  },

  async connectLink(returnTo) {
    const query = new URLSearchParams();
    if (returnTo) query.set('return_to', returnTo);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiRequest(`/api/calendar/google/connect-link${suffix}`);
  },

  async disconnectGoogle() {
    return apiRequest('/api/calendar/google', { method: 'DELETE' });
  },

  async calendars() {
    return apiRequest('/api/calendar/google/calendars');
  },

  async selectCalendar(calendarId) {
    return apiRequest('/api/calendar/google/calendar', {
      method: 'PATCH',
      body: JSON.stringify({ calendar_id: calendarId }),
    });
  },

  async events({ from, to } = {}) {
    const query = new URLSearchParams();
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiRequest(`/api/calendar/events${suffix}`);
  },
};

// ============== AUDIT ==============

export const audit = {
  async list(limit = 100) {
    return apiRequest(`/api/audit?limit=${limit}`);
  },
};

// Backwards-compatible named export used by older screens.
export const Audit = audit;

// ============== APPROVALS ==============

export const approvals = {
  async list(status = 'pending') {
    return apiRequest(`/api/approvals?status=${status}`);
  },
  async decide(id, decision, edited_text = null) {
    return apiRequest(`/api/approvals/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, edited_text }),
    });
  },
  async listComments(status = 'pending') {
    return apiRequest(`/api/approvals/comments?status=${encodeURIComponent(status)}`);
  },
  async updateCommentDraft(id, { selected_variant_id = null, edited_text = null } = {}) {
    return apiRequest(`/api/approvals/comments/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ selected_variant_id, edited_text }),
    });
  },
  async decideComment(id, decision, edited_text = null) {
    return apiRequest(`/api/approvals/comments/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, edited_text }),
    });
  },

  async markCommentPosted(id) {
    return apiRequest(`/api/approvals/comments/${encodeURIComponent(id)}/mark-posted`, {
      method: 'POST',
    });
  },

  async previewCommentSchedule(items, timezone) {
    return apiRequest('/api/approvals/comments/schedule-preview', {
      method: 'POST',
      body: JSON.stringify({ items, timezone }),
    });
  },

  async bulkScheduleComments(items, timezone, previewId = null) {
    return apiRequest('/api/approvals/comments/bulk-schedule', {
      method: 'POST',
      body: JSON.stringify({ items, timezone, preview_id: previewId }),
    });
  },
  async decideWithAttachment(id, { decision, edited_text = null, file = null }) {
    const formData = new FormData();
    formData.append('decision', decision);
    if (edited_text != null) formData.append('edited_text', edited_text);
    if (file) formData.append('file', file);
    return apiRequest(`/api/approvals/${id}/decision-with-attachment`, {
      method: 'POST',
      body: formData,
    });
  },

  // ---------- Post approvals (admin → client) ----------
  async listPosts(status = 'pending') {
    return apiRequest(`/api/approvals/posts?status=${status}`);
  },
  async decidePost(id, { decision, edited_text = null, edited_scheduled_at = null, rejection_comment = null }) {
    return apiRequest(`/api/approvals/posts/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, edited_text, edited_scheduled_at, rejection_comment }),
    });
  },
  postMediaUrl(id) {
    return `${API_URL}/api/approvals/posts/${id}/media`;
  },
  // Public token-based (for /approve/:token email landing)
  async fetchByToken(token) {
    return apiRequest(`/api/approvals/posts/email/${encodeURIComponent(token)}`);
  },
  async decideByToken(token, { decision, edited_text = null, edited_scheduled_at = null, rejection_comment = null }) {
    return apiRequest(`/api/approvals/posts/email/${encodeURIComponent(token)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, edited_text, edited_scheduled_at, rejection_comment }),
    });
  },
};

// ============== POSTS ==============

export const posts = {
  async list() {
    return apiRequest('/api/posts');
  },
  async decideDraft(draftId, decision, edited_text = null) {
    return apiRequest(`/api/posts/drafts/${draftId}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, edited_text }),
    });
  },
};

export default {
  auth,
  user,
  linkedin,
  engagement,
  inbox,
  leads,
  admin,
  ai,
  reports,
  diagnostics,
  bookings,
  calendar,
  audit,
  approvals,
  posts,
};
